// src/services/replyAssistant.js
// Client side of the Reply Assistant (Admin → Replies). See
// docs/decisions/adr-comment-reply-assistant.md and docs/ux/comment-reply-assistant.md.
//
// The load-bearing rule: SENDING reuses the exact functions the class thread
// uses (postComment as coach + notifyCoachReply; deleteComment +
// removeCoachReplyNotification for undo). No new write path for replies exists,
// so a reply sent from the queue is byte-identical — doc shape, commentCount
// bumps, bell + push — to one typed in the thread.
//
// Draft bookkeeping (status flips, sent text, edited flag) lives on
// replyDrafts/{commentId}; that collection is admin-only by nature.

import { db, firebaseEnabled } from './firebase';
import {
    collection, doc, getDoc, getDocs, onSnapshot, query, where, limit as fsLimit,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    setDoc, updateDoc,
} from './firestoreWrite';
import { postComment, deleteComment } from './classComments';
import { notifyCoachReply, removeCoachReplyNotification } from './commentNotifications';

const OPEN_STATUSES = ['drafting', 'ready', 'failed'];
const MAX_REPLY_CHARS = 280;

// Mirrors the server DEFAULT_CONFIG — keep in lockstep (api/_reply-assistant.js).
export const REPLY_ASSISTANT_DEFAULTS = {
    enabled: false,
    model: '',
    voiceModel: '',
    voiceProfile: '',
    voiceProfileGeneratedAt: null,
    voiceProfileSampleSize: 0,
    windowDays: 7,
    nudgeEnabled: true,
    nudgeHoursCT: [17, 21],
};

const nowIso = () => new Date().toISOString();

// ── Queue subscription ──
// ONE listener for the admin surface (nav pill + tab share it). `in` without an
// orderBy uses the automatic index; sorting happens here. Newest comment first.
export function subscribeToReplyQueue(onUpdate) {
    if (!firebaseEnabled || !db) { onUpdate([]); return () => {}; }
    const q = query(collection(db, 'replyDrafts'), where('status', 'in', OPEN_STATUSES));
    return onSnapshot(q, (snap) => {
        const items = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(b.commentCreatedAt || '').localeCompare(String(a.commentCreatedAt || '')));
        onUpdate(items);
    }, (err) => {
        console.error('replyAssistant: queue subscription error:', err);
        onUpdate([]);
    });
}

export async function fetchSkippedDrafts() {
    if (!firebaseEnabled || !db) return [];
    try {
        const snap = await getDocs(query(collection(db, 'replyDrafts'), where('status', '==', 'skipped'), fsLimit(60)));
        return snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            // Comment-deleted skips are housekeeping, not something to restore.
            .filter((d) => d.skipReason !== 'comment-deleted')
            .sort((a, b) => String(b.skippedAt || b.updatedAt || '').localeCompare(String(a.skippedAt || a.updatedAt || '')));
    } catch (e) {
        console.error('replyAssistant: fetchSkippedDrafts failed:', e);
        return [];
    }
}

// ── Config ──
export async function fetchReplyAssistantConfig() {
    if (!firebaseEnabled || !db) return { ...REPLY_ASSISTANT_DEFAULTS };
    try {
        const snap = await getDoc(doc(db, 'replyAssistant', 'config'));
        return { ...REPLY_ASSISTANT_DEFAULTS, ...(snap.exists() ? snap.data() : {}) };
    } catch (e) {
        console.error('replyAssistant: fetchReplyAssistantConfig failed:', e);
        return { ...REPLY_ASSISTANT_DEFAULTS };
    }
}

export async function updateReplyAssistantConfig(partial) {
    if (!firebaseEnabled || !db) return false;
    try {
        await setDoc(doc(db, 'replyAssistant', 'config'), { ...partial, updatedAt: nowIso() }, { merge: true });
        return true;
    } catch (e) {
        console.error('replyAssistant: updateReplyAssistantConfig failed:', e);
        return false;
    }
}

// ── Draft bookkeeping ──
async function patchDraft(id, data) {
    if (!firebaseEnabled || !db || !id) return false;
    try {
        await setDoc(doc(db, 'replyDrafts', id), { ...data, updatedAt: nowIso() }, { merge: true });
        return true;
    } catch (e) {
        console.error('replyAssistant: patchDraft failed:', e);
        return false;
    }
}

export function markDraftSkipped(id) {
    return patchDraft(id, { status: 'skipped', skippedAt: nowIso(), skipReason: 'admin' });
}

export function restoreDraft(id) {
    return patchDraft(id, { status: 'ready', skippedAt: null, skipReason: null });
}

// Danielle replied in the class thread itself → close the queue entry NOW, not
// at the next hourly sweep, so the card can't sit there inviting a second reply.
// updateDoc (not setDoc) so a comment with no draft doc is a silent no-op.
// Best-effort by contract: never throws, never touches the reply.
export async function markDraftAnswered(parentCommentId) {
    if (!firebaseEnabled || !db || !parentCommentId) return false;
    try {
        await updateDoc(doc(db, 'replyDrafts', parentCommentId), { status: 'answered_elsewhere', resolvedAt: nowIso(), updatedAt: nowIso() });
        return true;
    } catch (e) {
        return false; // no draft doc for this comment — nothing to close
    }
}

// Does the thread already carry a coach reply to this comment? One equality
// query (automatic index). Used as a last check before the queue posts.
async function threadHasCoachReply(parentCommentId) {
    if (!firebaseEnabled || !db || !parentCommentId) return false;
    try {
        const snap = await getDocs(query(collection(db, 'classComments'), where('parentCommentId', '==', parentCommentId)));
        return snap.docs.some((d) => { const x = d.data(); return x.isCoach && x.status !== 'deleted'; });
    } catch (e) {
        return false; // can't tell — don't block the send on a lookup failure
    }
}

// ── Send ──
// Exactly what ClassComments.handlePostReply does, minus the optimistic list.
// Returns the posted comment (with id) or null. The draft doc is flipped to
// `sent` AFTER the post succeeds; `edited` records whether Danielle changed the
// draft — that signal is what improves future drafts.
export async function sendDraftReply({ draft, text, userId, userName, getToken }) {
    const trimmed = (text || '').trim().slice(0, MAX_REPLY_CHARS);
    if (!draft || !trimmed || !userId) return null;
    // Guard against a double reply: if she already answered this one in the
    // thread (and the sweep hasn't caught up), close the card instead of posting.
    if (await threadHasCoachReply(draft.commentId || draft.id)) {
        await patchDraft(draft.id, { status: 'answered_elsewhere', resolvedAt: nowIso() });
        return { alreadyAnswered: true };
    }
    const safeName = userName || 'Danielle';
    const userInitials = safeName
        ? safeName.split(' ').map((n) => n.charAt(0).toUpperCase()).slice(0, 2).join('')
        : '?';
    const result = await postComment({
        userId,
        userName: safeName,
        userInitials,
        isCoach: true,
        classId: draft.classId || null,
        recordingId: draft.recordingId || null,
        text: trimmed,
        parentCommentId: draft.commentId || draft.id,
    });
    if (!result) return null;
    // Best-effort, fire-and-forget, never blocks — same contract as the thread.
    if (draft.memberUserId && draft.memberUserId !== userId && typeof getToken === 'function') {
        notifyCoachReply({
            recipientUserId: draft.memberUserId,
            classId: draft.classId || null,
            recordingId: draft.recordingId || null,
            vodAssetId: draft.recordingId || null,
            parentCommentId: draft.commentId || draft.id,
            parentText: draft.commentText || '',
            replyCommentId: result.id,
            replyText: result.text || trimmed,
        }, getToken);
    }
    const original = (draft.originalDraftText ?? draft.draftText ?? '').trim();
    await patchDraft(draft.id, {
        status: 'sent',
        sentAt: nowIso(),
        sentText: trimmed,
        sentCommentId: result.id,
        edited: trimmed !== original,
        originalDraftText: draft.originalDraftText ?? draft.draftText ?? '',
    });
    return result;
}

// ── Undo (≈6s window in the UI) ──
// Soft-deletes the just-posted reply through the thread's own path and drops
// its notification, then reopens the draft with HER text so nothing is lost.
export async function undoSentReply({ draft, sentCommentId, sentText, getToken }) {
    if (!draft || !sentCommentId) return false;
    const ok = await deleteComment(sentCommentId, draft.classId || null, draft.recordingId || null);
    if (!ok) return false;
    removeCoachReplyNotification(sentCommentId, getToken);
    await patchDraft(draft.id, {
        status: 'ready',
        sentAt: null,
        sentCommentId: null,
        sentText: null,
        draftText: sentText || draft.draftText || '',
        undoneAt: nowIso(),
    });
    return true;
}

// ── Admin endpoints ──
async function adminPost(url, body, getToken) {
    if (typeof getToken !== 'function') throw new Error('Not signed in');
    const token = await getToken();
    const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
}

export function requestRedraft(commentId, getToken) {
    return adminPost('/api/reply-assistant-draft', { commentId }, getToken);
}

export function generateVoiceProfile(getToken, model) {
    return adminPost('/api/reply-assistant-voice', model ? { model } : {}, getToken);
}

export function runReplySweepNow(getToken) {
    return adminPost('/api/reply-assistant-sweep', {}, getToken);
}
